/**
 * @fileoverview Suggestions panel showing potential companion cases.
 *
 * Displays:
 * 1. Minor alert: Warning if client is under 18 (may have parent/guardian case)
 * 2. Similar matters: Recent matters from same firm with similar last name
 */
import { AlertTriangle, Link2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { BaseMatter } from '@/services/types/client-intake-types';

interface PotentialCompanionsPanelProps {
  isMinor: boolean;
  similarMatters: BaseMatter[];
  onLinkToMatter: (matterId: string) => void;
  isLinking: boolean;
}

export const PotentialCompanionsPanel: React.FC<PotentialCompanionsPanelProps> = ({
  isMinor,
  similarMatters,
  onLinkToMatter,
  isLinking,
}) => {
  const hasSuggestions = isMinor || similarMatters.length > 0;

  if (!hasSuggestions) return null;

  return (
    <div className="space-y-1.5">
      {/* Minor warning banner */}
      {isMinor && (
        <div className="flex items-start gap-1.5 px-2 py-1.5 rounded-md border border-amber-200 bg-amber-50">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            Client is under 18. May be part of a companion case with a parent or guardian.
          </p>
        </div>
      )}

      {/* Similar matters list */}
      {similarMatters.length > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 overflow-hidden">
          <div className="px-2 py-1.5 bg-blue-100 border-b border-blue-200 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
            <p className="text-xs font-medium text-blue-800">
              Similar matters from this firm (last 48 hours)
            </p>
          </div>
          <div className="divide-y divide-blue-100">
            {similarMatters.map((matter) => (
              <div
                key={matter.id}
                className="flex items-center justify-between px-2 py-1.5 bg-white"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-xs font-medium text-zinc-900 truncate">
                    {matter.name}
                  </span>
                  {matter.created_at && (
                    <span className="text-xs text-zinc-500 flex-shrink-0">
                      {new Date(matter.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs ml-2 flex-shrink-0"
                  onClick={() => onLinkToMatter(matter.id)}
                  disabled={isLinking}
                >
                  <Link2 className="h-3 w-3 mr-1" />
                  Link
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
